import { Component } from "react";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import withNavigate from "../../components/withNavigate";
import UserManagementService from "../../services/api/usermanagement";
import { user } from "../../settings/shared/CommonMessages";
import { checkSpecialChar } from "../../settings/shared/commonMethods";
import { hasHTMLTags, validEmailRegex, default_size } from "../../settings/shared/constants";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import SAModalConfirm from "../../components/common/SAModalConfirm";
import toast from "react-hot-toast";
import { MdOutlineClose } from "react-icons/md";
import { isPocTenant } from "../../utils/premiumAccess";
import SAPagination from "../../components/common/SAPagination";
import moment from "moment";



/* ─────────────────────────────────────────────────────────────
   Static config
───────────────────────────────────────────────────────────── */
const ROLE_META = {
  TENANT_ADMIN: { label: "Admin", bg: "#e6f1fb", color: "#185fa5" },
  TENANT_MANAGEMENT: { label: "Management", bg: "#fbeaf0", color: "#993556" },
  GENERAL_USER: { label: "General", bg: "#eaf3de", color: "#3b6d11" },
  DEVELOPER: { label: "Developer", bg: "#f1efe8", color: "#5f5e5a" },
};

const AVATAR_PALETTE = [
  { bg: "#eeedfe", color: "#534ab7" },
  { bg: "#e6f1fb", color: "#185fa5" },
  { bg: "#fbeaf0", color: "#993556" },
  { bg: "#eaf3de", color: "#3b6d11" },
  { bg: "#faeeda", color: "#854f0b" },
  { bg: "#f1efe8", color: "#5f5e5a" },
];

const STAT_TABS = [
  {
    key: "all",
    label: "Total",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="5" cy="5" r="2.5" stroke="#534ab7" strokeWidth="1.2" />
        <circle cx="11" cy="5" r="2.5" stroke="#534ab7" strokeWidth="1.2" />
        <path d="M1 13c0-2.2 1.8-4 4-4h6c2.2 0 4 1.8 4 4" stroke="#534ab7" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
    iconBg: "#eeedfe",
    countKey: "total",
  },
  {
    key: "TENANT_ADMIN",
    label: "Admins",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="3" stroke="#185fa5" strokeWidth="1.2" />
        <path d="M2 14c0-2.5 2.2-4.5 6-4.5s6 2 6 4.5" stroke="#185fa5" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M8 9.5l1 2.5-1 1-1-1 1-2.5z" fill="#185fa5" />
      </svg>
    ),
    iconBg: "#e6f1fb",
    countKey: "admin",
  },
  {
    key: "TENANT_MANAGEMENT",
    label: "Management",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="3" stroke="#993556" strokeWidth="1.2" />
        <path d="M2 14c0-2.5 2.2-4.5 6-4.5s6 2 6 4.5" stroke="#993556" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="10" y1="11" x2="14" y2="11" stroke="#993556" strokeWidth="1.2" strokeLinecap="round" />
        <line x1="10" y1="13" x2="13" y2="13" stroke="#993556" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
    iconBg: "#fbeaf0",
    countKey: "management",
  },
  {
    key: "GENERAL_USER",
    label: "General",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="3" stroke="#3b6d11" strokeWidth="1.2" />
        <path d="M2 14c0-2.5 2.2-4.5 6-4.5s6 2 6 4.5" stroke="#3b6d11" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
    iconBg: "#eaf3de",
    countKey: "general",
  },
  {
    key: "DEVELOPER",
    label: "Developers",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <polyline points="4,5 1,8 4,11" stroke="#5f5e5a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="12,5 15,8 12,11" stroke="#5f5e5a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="9" y1="3" x2="7" y2="13" stroke="#5f5e5a" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    ),
    iconBg: "#f1efe8",
    countKey: "developer",
  },
];

/* ─────────────────────────────────────────────────────────────
   Inline styles object (scoped, no external CSS needed)
───────────────────────────────────────────────────────────── */
const S = {
  root: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    minHeight: "100vh",
    color: "#1a1a2e",
  },
  // ── Top bar ──
  topbar: {
    background: "#fff",
    borderBottom: "0.5px solid rgba(0,0,0,0.08)",
    padding: "0 28px",
    height: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  topbarLeft: { display: "flex", alignItems: "center", gap: 10 },
  orgDot: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "var(--activemenu, #3d91e4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "-0.5px",
    flexShrink: 0,
  },
  orgName: { fontSize: 14, fontWeight: 500, color: "#1a1a2e" },
  topbarRight: { display: "flex", alignItems: "center", gap: 12 },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#f5f5f3",
    border: "0.5px solid rgba(0,0,0,0.1)",
    borderRadius: 8,
    padding: "0 12px",
    height: 36,
    width: 240,
    transition: "border-color 0.15s",
  },
  searchInput: {
    border: "none",
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 13,
    color: "#1a1a2e",
    outline: "none",
    width: "100%",
  },
  btnAdd: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "var(--activemenu, #3d91e4)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0 14px",
    height: 36,
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // ── Body ──
  body: { padding: "24px 28px" },
  // ── Stat cards ──
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 12,
    marginBottom: 24,
  },
  statCard: (active) => ({
    background: "#fff",
    border: active ? "1.5px solid var(--activemenu, #3d91e4)" : "0.5px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    cursor: "pointer",
    transition: "border-color 0.15s",
  }),
  statIcon: (bg) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  statLabel: {
    fontSize: 11,
    color: "#888",
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statCount: {
    fontSize: 22,
    fontWeight: 500,
    color: "#1a1a2e",
    fontFamily: "'DM Mono', 'Courier New', monospace",
  },
  // ── Table ──
  tableWrap: {
    background: "#fff",
    border: "0.5px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "12px 20px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#888",
    background: "#f9f9f8",
    borderBottom: "0.5px solid rgba(0,0,0,0.08)",
  },
  thRight: {
    padding: "12px 20px",
    textAlign: "right",
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    color: "#888",
    background: "#f9f9f8",
    borderBottom: "0.5px solid rgba(0,0,0,0.08)",
  },
  td: { padding: "14px 20px", verticalAlign: "middle", borderBottom: "0.5px solid rgba(0,0,0,0.06)" },
  tdRight: { padding: "14px 20px", verticalAlign: "middle", borderBottom: "0.5px solid rgba(0,0,0,0.06)", textAlign: "right" },
  userCell: { display: "flex", alignItems: "center", gap: 12 },
  avatar: (bg, color) => ({
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: bg,
    color: color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 500,
    flexShrink: 0,
  }),
  userName: { fontSize: 13, fontWeight: 500, color: "#1a1a2e" },
  userEmail: { fontSize: 12, color: "#888", marginTop: 2 },
  rolePill: (bg, color) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 500,
    background: bg,
    color: color,
    whiteSpace: "nowrap",
  }),
  badgeVerified: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 500,
    background: "#eaf3de",
    color: "#3b6d11",
  },
  badgePending: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 500,
    background: "#faeeda",
    color: "#854f0b",
  },
  actionBtn: (danger) => ({
    width: 30,
    height: 30,
    border: "0.5px solid rgba(0,0,0,0.1)",
    borderRadius: 7,
    background: "transparent",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: danger ? "#a32d2d" : "#888",
    marginLeft: 4,
    transition: "background 0.1s, color 0.1s",
  }),
  // ── Empty state ──
  empty: { textAlign: "center", padding: "64px 20px" },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "#f5f5f3",
    margin: "0 auto 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyTitle: { fontSize: 15, fontWeight: 500, marginBottom: 6 },
  emptySub: { fontSize: 13, color: "#888", marginBottom: 20 },
  // ── Slide panel ──
  panelOverlay: (open) => ({
    display: open ? "block" : "none",
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    zIndex: 50,
  }),
  panel: (open) => ({
    position: "fixed",
    top: 0,
    right: 0,
    width: 420,
    maxWidth: "100vw",
    height: "100vh",
    background: "#fff",
    borderLeft: "0.5px solid rgba(0,0,0,0.12)",
    zIndex: 51,
    display: "flex",
    flexDirection: "column",
    transform: open ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.22s ease",
  }),
  panelHeader: {
    padding: "20px 24px",
    borderBottom: "0.5px solid rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  panelTitle: { fontSize: 15, fontWeight: 500 },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 7,
    border: "0.5px solid rgba(0,0,0,0.1)",
    background: "transparent",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#888",
  },
  panelBody: { padding: 24, flex: 1, overflowY: "auto" },
  panelFooter: {
    padding: "16px 24px",
    borderTop: "0.5px solid rgba(0,0,0,0.08)",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  fieldRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  field: { marginBottom: 18 },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#888",
    marginBottom: 6,
  },
  required: { color: "#a32d2d" },
  input: (error) => ({
    width: "100%",
    height: 36,
    padding: "0 12px",
    border: error ? "0.5px solid #e24b4a" : "0.5px solid rgba(0,0,0,0.1)",
    borderRadius: 8,
    background: "#f9f9f8",
    fontFamily: "inherit",
    fontSize: 13,
    color: "#1a1a2e",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
  }),
  errorMsg: { fontSize: 11, color: "#a32d2d", marginTop: 4 },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "var(--activemenu, #3d91e4)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0 16px",
    height: 36,
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnSecondary: {
    padding: "0 14px",
    height: 36,
    border: "0.5px solid rgba(0,0,0,0.15)",
    borderRadius: 8,
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 13,
    cursor: "pointer",
    color: "#1a1a2e",
  },
};

/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
function getInitials(u) {
  return (((u.firstName || "")[0] || "") + ((u.lastName || "")[0] || "")).toUpperCase() || "?";
}
function avatarPalette(id) {
  let index = 0;
  if (id !== undefined && id !== null) {
    if (typeof id === "number" && !Number.isNaN(id)) {
      index = id;
    } else {
      const str = String(id);
      for (let i = 0; i < str.length; i++) index = (index * 31 + str.charCodeAt(i)) >>> 0;
    }
  }
  return AVATAR_PALETTE[index % AVATAR_PALETTE.length];
}
function getOrgInitials() {
  try {
    const name = JSON.parse(localStorage.getItem("profile"))?.accountName || "AC";
    return name.slice(0, 2).toUpperCase();
  } catch {
    return "AC";
  }
}
function getOrgName() {
  try {
    return JSON.parse(localStorage.getItem("profile"))?.accountName || "Organization";
  } catch {
    return "Organization";
  }
}

/* ─────────────────────────────────────────────────────────────
   Icons
───────────────────────────────────────────────────────────── */
const IconSearch = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
    <line x1="10.5" y1="10.5" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const IconPlus = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconCheck = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <polyline points="1.5,5 4,7.5 8.5,2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconClock = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.3" />
    <line x1="5" y1="3" x2="5" y2="5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <circle cx="5" cy="7" r="0.6" fill="currentColor" />
  </svg>
);
const IconSave = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <polyline points="1,6 4.5,9.5 11,2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
class UserList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      userdetails: [],
      loading: false,
      searchValue: "",
      activeRole: "all",
      pageNumber: 0,
      pageSize: default_size,
      totalPages: 0,
      totalElements: 0,
      // Panel (create user)
      showPanel: false,
      // Form fields
      firstname: "",
      lastname: "",
      email: "",
      roleName: "",
      url: "",
      formErrors: {},
      isSubmitting: false,
      // Delete modal
      showDeleteModal: false,
      deleteId: "",
      deleteusername: "",
    };
    this._isMounted = false;
    this.abortController = null;
    this.searchDebounceTimer = null;
  }

  componentDidMount() {
    this._isMounted = true;
    this.getUserList();
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.abortController) this.abortController.abort();
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  }

  /* ── API ── */
  getUserList = () => {
  if (this.abortController) {
    this.abortController.abort();
  }
  this.abortController = new AbortController();

  this.setState({ userdetails: [], loading: true });

  const accountId = localStorage.getItem("accountId");
  if (!accountId) {
    if (this._isMounted) this.setState({ loading: false });
    toast.error("Account information missing. Please login again.");
    return;
  }

  const { pageNumber, pageSize } = this.state;

  UserManagementService.getuserlist(accountId, pageNumber, pageSize)
    .then((response) => {
      if (!this._isMounted) return;
      if (response?.status === "SUCCESS" && response.userDTOS) {
        const page = response.userDTOPage;
        this.setState({
          loading: false,
          userdetails: response.userDTOS,
          totalPages: page?.totalPages ?? 0,
          totalElements: page?.totalElements ?? response.userDTOS.length,
        });
      } else {
        this.setState({ loading: false });
        toast.error(response?.errorMessage || "Failed to load users");
      }
    })
    .catch((error) => {
      if (!this._isMounted || error.name === "AbortError") return;
      this.setState({ loading: false });
      toast.error("Error loading users. Please try again.");
      console.error(error);
    });
};

  /* ── Delete ── */
  showDeleteModal = (e, uData) => {
    e.preventDefault();
    this.setState({
      deleteId: uData?.id,
      deleteusername: `${uData?.firstName || ""} ${uData?.lastName || ""}`.trim(),
      showDeleteModal: true,
    });
  };

  handleDeleteOk = () => {
    if (!this.state.deleteId) return;
    UserManagementService.deleteuser(this.state.deleteId)
      .then((response) => {
        if (!this._isMounted) return;
        if (response?.status === "SUCCESS") {
          toast.success("User removed successfully.");
          this.getUserList();
          this.setState({ showDeleteModal: false, deleteId: "", deleteusername: "" });
        } else {
          toast.error(response?.errorMessage || "Failed to remove user");
        }
      })
      .catch((error) => {
        if (!this._isMounted) return;
        toast.error("Error removing user. Please try again.");
        console.error(error);
      });
  };

  handleDeleteCancel = () => {
    this.setState({ showDeleteModal: false, deleteId: "", deleteusername: "" });
  };

  /* ── Search / filter ── */
  handleSearchChange = (e) => {
    this.setState({ searchValue: e.target.value });
  };

  setActiveRole = (role) => {
    this.setState({ activeRole: role });
  };

  goToPage = (newPage) => {
    this.setState({ pageNumber: newPage }, this.getUserList);
  };

  getFilteredUsers = () => {
    const { userdetails, searchValue, activeRole } = this.state;
    let list = userdetails;
    if (activeRole !== "all") list = list.filter((u) => u.role[0] === activeRole || (u.roles && u.roles.includes(activeRole)));
    if (!searchValue.trim()) return list;
    const q = searchValue.toLowerCase().trim();
    return list.filter((u) => {
      const fn = (u.firstName || "").toLowerCase();
      const ln = (u.lastName || "").toLowerCase();
      const em = (u.email || "").toLowerCase();
      return fn.includes(q) || ln.includes(q) || `${fn} ${ln}`.includes(q) || em.includes(q);
    });
  };

  getStats = () => {
    const { userdetails } = this.state;
    return {
      total: userdetails.length,
      admin: userdetails.filter((u) => u?.role[0] === "TENANT_ADMIN").length,
      management: userdetails.filter((u) => u?.role[0] === "TENANT_MANAGEMENT").length,
      general: userdetails.filter((u) => u?.role[0] === "GENERAL_USER").length,
      developer: userdetails.filter((u) => u?.role[0] === "DEVELOPER").length,
    };
  };

  /* ── Create user panel ── */
  openPanel = () => this.setState({ showPanel: true, firstname: "", lastname: "", email: "", roleName: "", url: "", formErrors: {} });
  closePanel = () => this.setState({ showPanel: false, formErrors: {} });

  handleFormChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value, formErrors: { ...this.state.formErrors, [name]: "" } });
  };

  validateForm = () => {
    const errors = {};
    const fn = (this.state.firstname || "").trim();
    const em = (this.state.email || "").trim();

    if (!fn) errors.firstname = user.nameEmpty || "First name is required.";
    else if (fn.length < 2) errors.firstname = user.userNameLength || "Minimum 2 characters.";
    else if (hasHTMLTags(fn)) errors.firstname = user.htmlTags || "HTML tags are not allowed.";
    else if (!checkSpecialChar(fn)) errors.firstname = user.specialChar || "Special characters are not allowed.";

    if (!em) errors.email = user.emailEmpty || "Email is required.";
    else if (!validEmailRegex.test(em)) errors.email = user.invalidemail || "Enter a valid email address.";

    if (!this.state.roleName || this.state.roleName === "Select Role") {
      errors.roleName = user.roleEmpty || "Please select a role.";
    }

    return errors;
  };

  sanitizeInput = (input) => (input ? input.trim().replace(/[<>]/g, "") : "");

  createUser = async (e) => {
    e.preventDefault();
    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ formErrors: errors });
      return;
    }
    if (this.state.isSubmitting) return;
    this.setState({ isSubmitting: true });

    try {
      const tenant = localStorage.getItem("tenant");
      const accountId = localStorage.getItem("accountId");
      if (!tenant || !accountId) {
        toast.error("Session information missing. Please login again.");
        this.setState({ isSubmitting: false });
        return;
      }

      const post = {
        firstName: this.sanitizeInput(this.state.firstname),
        lastName: this.sanitizeInput(this.state.lastname),
        email: this.state.email.trim().toLowerCase(),
        tenant,
        authProvider: "platform",
        role: [this.state.roleName],
        organisation: accountId,
      };

      const response = await UserManagementService.createuser(post);
      if (!this._isMounted) return;

      if (response?.status === "SUCCESS") {
        toast.success("User created successfully!");
        this.getUserList();
        this.closePanel();
      } else {
        showNotificationGlobal(response?.errorMessage || "Failed to create user", "error");
      }
    } catch (error) {
      if (!this._isMounted) return;
      toast.error("Error creating user. Please try again.");
      console.error(error);
    } finally {
      if (this._isMounted) this.setState({ isSubmitting: false });
    }
  };

  /* ── Render helpers ── */
  renderStatCards() {
    const stats = this.getStats();
    return (
      <div style={S.statsGrid}>
        {STAT_TABS.map((tab) => (
          <div
            key={tab.key}
            style={S.statCard(this.state.activeRole === tab.key)}
            onClick={() => this.setActiveRole(tab.key)}
          >
            <div style={S.statIcon(tab.iconBg)}>{tab.icon}</div>
            <span style={S.statLabel}>{tab.label}</span>
            <span style={S.statCount}>{stats[tab.countKey]}</span>
          </div>
        ))}
      </div>
    );
  }

  renderTable() {
    const filteredUsers = this.getFilteredUsers();
    const profile = JSON.parse(localStorage.getItem("profile") || "{}");
    const currentUserId = profile.userId;

    if (this.state.loading) {
      return (
        <div style={{ ...S.empty }}>
          <div style={{ ...S.emptyTitle, color: "#888" }}>Loading users…</div>
        </div>
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <div style={S.empty}>
          <div style={S.emptyIcon}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="8" cy="8" r="6" stroke="#888" strokeWidth="1.5" />
              <path d="M13 13l4 4" stroke="#888" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <div style={S.emptyTitle}>
            {this.state.searchValue ? "No results found" : "No users yet"}
          </div>
          <p style={S.emptySub}>
            {this.state.searchValue
              ? "Try a different name or email."
              : "Add your first user to get started."}
          </p>
          {/* {!this.state.searchValue && !isPocTenant() && ( */}
            <button style={S.btnPrimary} onClick={this.openPanel}>
              <IconPlus /> Add user
            </button>
          {/* )} */}
        </div>
      );
    }

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>User</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Last Login</th>
              <th style={S.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((uData, idx) => {
              const id = uData?.id || uData?.email;
              const isCurrentUser =
                String(uData?.id) === String(currentUserId);
              const firstName = uData?.firstName || "";
              const lastName = uData?.lastName || "";
              const fullName = `${firstName} ${lastName}`.trim() || "Unknown";
              const av = avatarPalette(uData?.id || idx);
              const role = ROLE_META[uData?.role] || { label: uData?.role || "—", bg: "#f1efe8", color: "#5f5e5a" };
              const isLast = idx === filteredUsers.length - 1;
              const tdStyle = isLast
                ? { ...S.td, borderBottom: "none" }
                : S.td;
              const tdRightStyle = isLast
                ? { ...S.tdRight, borderBottom: "none" }
                : S.tdRight;

              return (
                <tr key={id} style={{ transition: "background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#fafaf9"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={tdStyle}>
                    <div style={S.userCell}>
                      <div style={S.avatar(av.bg, av.color)}>
                        {getInitials(uData)}
                      </div>
                      <div>
                        <div style={S.userName}>{fullName}</div>
                        <div style={S.userEmail}>{uData?.email || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={S.rolePill(role.bg, role.color)}>{role.label}</span>
                  </td>
                  <td style={tdStyle}>
                    {uData?.verified ? (
                      <span style={S.badgeVerified}>
                        <IconCheck /> Verified
                      </span>
                    ) : (
                      <span style={S.badgePending}>
                        <IconClock /> Pending
                      </span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {uData?.lastLogin ? (
                      moment
                        .utc(uData.lastLogin)
                        .local()
                        .format("MMM DD, YYYY · hh:mm A")
                    ) : (
                      <span style={{ color: "#aaa" }}>
                        Not logged in yet
                      </span>
                    )}
                  </td>
                  <td style={tdRightStyle}>
                    <button
                      style={{
                        ...S.actionBtn(false),
                        opacity: 0.35,
                        cursor: "not-allowed"
                      }}
                      title="Edit (coming soon)"
                      disabled
                    >
                      <LuPencil size={14} style={{ flexShrink: 0 }} />
                    </button>

                    <button
                      style={{
                        ...S.actionBtn(true),
                        opacity: isCurrentUser ? 0.35 : 1,
                        cursor: isCurrentUser ? "not-allowed" : "pointer",
                      }}
                      title={ 
                        isCurrentUser
                          ? "You cannot remove yourself"
                          : `Remove ${fullName}`
                      }
                      disabled={isCurrentUser}
                      onClick={(e) => this.showDeleteModal(e, uData)}
                      onMouseEnter={e => {
                        if (!isCurrentUser) {
                          e.currentTarget.style.background = "#fcebeb";
                          e.currentTarget.style.borderColor = "#f09595";
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isCurrentUser) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)";
                        }
                      }}
                    >
                      <LuTrash2 size={14} style={{ flexShrink: 0 }} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  renderPagination() {
  const { pageNumber, totalPages, totalElements, pageSize } = this.state;
  if (totalPages <= 1) return null;

  return (
    <div style={{ padding: "12px 20px", borderTop: "0.5px solid rgba(0,0,0,0.08)" }}>
      <SAPagination
        currentPage={pageNumber}
        totalPages={totalPages}
        totalItems={totalElements}
        itemsPerPage={pageSize}
        onPageChange={this.goToPage}
        showGoToPage={false}
        showPageInfo={true}
        size="sm"
      />
    </div>
  );
}

  renderPanel() {
    const { showPanel, formErrors, isSubmitting } = this.state;
    return (
      <>
        {/* Overlay */}
        <div
          style={S.panelOverlay(showPanel)}
          onClick={this.closePanel}
        />
        {/* Slide panel */}
        <div style={S.panel(showPanel)}>
          <div style={S.panelHeader}>
            <span style={S.panelTitle}>Add new user</span>
            <button style={S.closeBtn} onClick={this.closePanel}>
              <MdOutlineClose size={14} style={{ flexShrink: 0 }} />
            </button>
          </div>

          <div style={S.panelBody}>
            {/* Name row */}
            <div style={S.fieldRow}>
              <div style={S.field}>
                <label style={S.label}>
                  First name <span style={S.required}>*</span>
                </label>
                <input
                  style={S.input(!!formErrors.firstname)}
                  type="text"
                  name="firstname"
                  placeholder="First name"
                  value={this.state.firstname}
                  onChange={this.handleFormChange}
                  autoComplete="off"
                />
                {formErrors.firstname && (
                  <div style={S.errorMsg}>{formErrors.firstname}</div>
                )}
              </div>
              <div style={S.field}>
                <label style={S.label}>Last name</label>
                <input
                  style={S.input(false)}
                  type="text"
                  name="lastname"
                  placeholder="Last name"
                  value={this.state.lastname}
                  onChange={this.handleFormChange}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Email */}
            <div style={S.field}>
              <label style={S.label}>
                Email address <span style={S.required}>*</span>
              </label>
              <input
                style={S.input(!!formErrors.email)}
                type="email"
                name="email"
                placeholder="Email address"
                value={this.state.email}
                onChange={this.handleFormChange}
                autoComplete="off"
              />
              {formErrors.email && (
                <div style={S.errorMsg}>{formErrors.email}</div>
              )}
            </div>

            {/* Role */}
            <div style={S.field}>
              <label style={S.label}>
                Role <span style={S.required}>*</span>
              </label>
              <select
                style={{ ...S.input(!!formErrors.roleName), cursor: "pointer" }}
                name="roleName"
                value={this.state.roleName}
                onChange={this.handleFormChange}
              >
                <option value="">Select a role…</option>
                <option value="TENANT_ADMIN">Tenant Admin</option>
                <option value="TENANT_MANAGEMENT">Tenant Management</option>
                <option value="GENERAL_USER">General User</option>
                <option value="DEVELOPER">Developer</option>
              </select>
              {formErrors.roleName && (
                <div style={S.errorMsg}>{formErrors.roleName}</div>
              )}
            </div>

            {/* URL */}
            <div style={S.field}>
              <label style={S.label}>
                URL{" "}
                <span style={{ color: "#aaa", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                  (optional)
                </span>
              </label>
              <input
                style={S.input(false)}
                type="url"
                name="url"
                placeholder="https://…"
                value={this.state.url}
                onChange={this.handleFormChange}
                autoComplete="off"
              />
            </div>
          </div>

          <div style={S.panelFooter}>
            <button style={S.btnSecondary} onClick={this.closePanel} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              style={{ ...S.btnPrimary, opacity: isSubmitting ? 0.6 : 1 }}
              onClick={this.createUser}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    style={{ width: 10, height: 10, border: "2px solid #ffffff55", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }}
                  />
                  Creating…
                </>
              ) : (
                <>
                  <IconSave /> Create user
                </>
              )}
            </button>
          </div>
        </div>
      </>
    );
  }

  render() {
    const orgInitials = getOrgInitials();
    const orgName = getOrgName();

    return (
      <>
        {/* Spinner keyframe — injected once */}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        <div style={S.root}>
          {/* ── Top bar ── */}
          <div style={S.topbar}>
            <div style={S.topbarLeft}>
              <div style={S.orgDot}>{orgInitials}</div>
              <span style={S.orgName}>{orgName}</span>
            </div>
            <div style={S.topbarRight}>
              {/* Search */}
              <div style={S.searchBox}>
                <span style={{ color: "#aaa", display: "flex" }}>
                  <IconSearch />
                </span>
                <input
                  style={S.searchInput}
                  type="text"
                  placeholder="Search by name or email…"
                  value={this.state.searchValue}
                  onChange={this.handleSearchChange}
                  autoComplete="off"
                />
              </div>
              {/* {!isPocTenant() && ( */}
                <button style={S.btnAdd} onClick={this.openPanel}>
                  <IconPlus /> Add user
                </button>
              {/* )} */}
            </div>
          </div>

          {/* ── Body ── */}
          <div style={S.body}>
            {this.renderStatCards()}

            <div style={S.tableWrap}>
              {this.renderTable()}
              {this.renderPagination()}
            </div>

          </div>
        </div>

        {/* ── Side panel ── */}
        {this.renderPanel()}

        {/* ── Delete modal ── */}
        <SAModalConfirm
          show={this.state.showDeleteModal}
          title="Remove user"
          message={`Remove ${this.state.deleteusername} from the organization? This action cannot be undone.`}
          okText="Remove user"
          cancelText="Cancel"
          handleOk={this.handleDeleteOk}
          handleCancel={this.handleDeleteCancel}
        />
      </>
    );
  }
}

export default withNavigate(UserList);